<template>
  <div class="repository-collaborators">
    <CollaboratorsForm
      v-if="!isReadonly"
      :repository-id
      :list="items"
      :disabled
    />
    <div class="repository-collaborators__caption">
      Repository collaborators
    </div>
    <div class="repository-collaborators__container">
      <UIGridSkeleton
        v-if="isLoading"
        :columns
        :sm
        wrapped
        message="Loading collaborators..."
      />
      <UIGrid
        v-else
        v-model:sort="sort"
        :columns
        :sm
        :edit-row-id="editRowId"
        :items
        unitary-sort
        wrapped
        editable-rows
        id-field="email"
      >
        <template #cellAccessType="{ displayValue, editRow, item }">
          <CollaboratorsAccess
            :is-readonly
            :display-value
            :edit-row
            :item
            :repository-id
            @edit="handleEdit"
          />
        </template>
        <template #actions="{ item, size }">
          <CollaboratorsButtons
            v-if="isButtonsVisible(item)"
            :item
            :size
            :current-user-id
            :repository-id
          />
        </template>
      </UIGrid>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import {
  Repository,
  RepositoryCollaborator,
  RepositoryInvite,
  Sort,
} from '@types'

import { ACCESS_TYPE_OWNER, ACCESS_TYPE_READONLY } from '@/const'

import useGridSort from '@/components/hooks/gridSort'

import { useRepositoriesCollaboratorsStore } from '@/store/repositories/collaborators'
import { useUserStore } from '@/store/user'

import { UIGrid, UIGridSkeleton } from '@ui/grid'

import CollaboratorsAccess from './CollaboratorsAccess.vue'
import CollaboratorsButtons from './CollaboratorsButtons.vue'
import CollaboratorsForm from './CollaboratorsForm.vue'

type Props = {
  repository: Repository
}

const props = defineProps<Props>()

const repositoriesCollaboratorsStore = useRepositoriesCollaboratorsStore()
const userStore = useUserStore()

const sort = ref<Sort[]>()

const editedItem = ref<RepositoryCollaborator>()

const disabled = computed(() => repositoriesCollaboratorsStore.loadingAction)

const isReadonly = computed(
  () =>
    props.repository.settings?.readonly ||
    !props.repository.user_repo_settings?.access_type ||
    ACCESS_TYPE_READONLY === props.repository.user_repo_settings?.access_type,
)

const editRowId = computed(() => editedItem.value?.email)

const currentUserId = computed(() => userStore.getUserId)

const repositoryId = computed(() => props.repository.id)

const sm = '1fr'
const columns = computed(() => [
  {
    name: 'email',
    caption: 'Email',
  },
  {
    name: 'access_type',
    caption: 'Permissions',
    default: '120px',
  },
  {
    name: 'status',
    caption: 'Status',
    default: '110px',
  },
])

const inputItems = computed(() => repositoriesCollaboratorsStore.getList)

const items = useGridSort(sort, columns, inputItems, ['amount'])

const isLoading = computed<boolean>(
  () => repositoriesCollaboratorsStore.loading,
)

const isButtonsVisible = (item?: RepositoryInvite | RepositoryCollaborator) => {
  return (
    item?.access_type !== ACCESS_TYPE_OWNER &&
    (!isReadonly.value ||
      (item as RepositoryCollaborator)?.user?.id === userStore.getUserId)
  )
}

const handleEdit = (item?: RepositoryCollaborator) => {
  editedItem.value = item
}
</script>

<style>
.repository-collaborators {
  @apply space-y-2.5;

  &__caption {
    @apply font-medium;
  }
  
  &__container {
    @apply flex-auto;
    @apply -mx-2 md:mx-0;
  }
}
</style>
